import { useEffect } from "react";
import * as THREE from "three";
import {
  useMassPropertiesBodyComMarkersVisible,
  useMassPropertiesIsStale,
  useMassPropertiesRobotComMarkerVisible,
  useMassPropertiesRows,
  useMassPropertiesStatus,
  useMassPropertiesSummary,
} from "../../stores/massPropertiesStore";
import { useBodies, useWorldBodies } from "../../stores/sceneStore";
import { buildBodyRootMap } from "../../utils/bodyHierarchy";
import { computeLargestRobotDimension } from "../computeLargestRobotDimension";
import { disposeSceneObjectTree } from "../scene/sceneDispose";

const COM_MARKER_GROUP_NAME = "__massPropertiesComMarkers";
const ROBOT_COM_COLOR = 0xff4d8d;
const BODY_COM_COLOR = 0xfacc15;

const isFiniteVector3 = (value) =>
  Array.isArray(value) &&
  value.length >= 3 &&
  value.slice(0, 3).every((component) => Number.isFinite(Number(component)));

function clearGroup(group) {
  if (!group) return;
  while (group.children.length > 0) {
    const child = group.children[0];
    group.remove(child);
    disposeSceneObjectTree(child);
  }
}

function createComMarker({ color, radius, name, position, userData }) {
  const marker = new THREE.Mesh(
    new THREE.SphereGeometry(radius, 18, 18),
    new THREE.MeshBasicMaterial({
      color,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
    })
  );
  marker.name = name;
  marker.userData = {
    ...userData,
    isMassPropertiesComMarker: true,
  };
  marker.position.set(Number(position[0]) || 0, Number(position[1]) || 0, Number(position[2]) || 0);
  marker.renderOrder = 10020;
  return marker;
}

function resolveMarkerParent(scene) {
  return scene?.getObjectByName("modelGroup") || null;
}

function removeMarkerGroup(root) {
  const group = root?.getObjectByName?.(COM_MARKER_GROUP_NAME);
  if (!group) return;
  clearGroup(group);
  group.parent?.remove(group);
}

function computeMarkerRadii(parent) {
  const largestDim = computeLargestRobotDimension(parent);
  const scale = Number.isFinite(largestDim) && largestDim > 0 ? largestDim : 1;
  return {
    robot: THREE.MathUtils.clamp(scale * 0.01, 0.016, 0.065),
    body: THREE.MathUtils.clamp(scale * 0.0065, 0.01, 0.04),
  };
}

function buildWorldBodyNameByUuid(worldBodies) {
  const names = {};
  (worldBodies || []).forEach((worldBody) => {
    if (!worldBody) return;
    if (typeof worldBody === "string") {
      names[worldBody] = "";
      return;
    }
    if (worldBody.uuid) names[worldBody.uuid] = worldBody.name || "";
  });
  return names;
}

function computeObjectComMarkers({ rows, bodies, worldBodies }) {
  const rootMap = buildBodyRootMap(bodies || {}, worldBodies || []);
  const worldBodyNameByUuid = buildWorldBodyNameByUuid(worldBodies);
  const groups = new Map();

  rows.forEach((row) => {
    const mass = Number(row?.mass) || 0;
    if (!(mass > 0) || !row?.bodyUuid || !isFiniteVector3(row.worldCom)) return;
    const rootUuid = rootMap[row.bodyUuid] || row.bodyUuid;
    if (!groups.has(rootUuid)) {
      groups.set(rootUuid, {
        rootUuid,
        mass: 0,
        weightedCom: [0, 0, 0],
        label:
          worldBodyNameByUuid[rootUuid] ||
          bodies?.[rootUuid]?.name ||
          row.displayName ||
          row.name ||
          "Object",
      });
    }
    const group = groups.get(rootUuid);
    group.mass += mass;
    group.weightedCom[0] += Number(row.worldCom[0]) * mass;
    group.weightedCom[1] += Number(row.worldCom[1]) * mass;
    group.weightedCom[2] += Number(row.worldCom[2]) * mass;
  });

  return Array.from(groups.values())
    .filter((group) => group.mass > 0)
    .map((group) => ({
      rootUuid: group.rootUuid,
      mass: group.mass,
      label: group.label,
      position: group.weightedCom.map((value) => value / group.mass),
    }));
}

export function useMassPropertiesComMarkers({ sceneRef, structureChangeTick }) {
  const rows = useMassPropertiesRows();
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const showRobotComMarker = useMassPropertiesRobotComMarkerVisible();
  const showBodyComMarkers = useMassPropertiesBodyComMarkersVisible();
  const status = useMassPropertiesStatus();
  const isStale = useMassPropertiesIsStale();
  const { computedAt } = useMassPropertiesSummary();

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return undefined;

    const markerParent = resolveMarkerParent(scene);
    if (!markerParent) {
      removeMarkerGroup(scene);
      return undefined;
    }

    let group = markerParent.getObjectByName(COM_MARKER_GROUP_NAME);
    if (!group) {
      group = new THREE.Group();
      group.name = COM_MARKER_GROUP_NAME;
      group.renderOrder = 10018;
      markerParent.add(group);
    }

    clearGroup(group);

    if (
      !computedAt ||
      isStale ||
      status === "computing" ||
      (!showRobotComMarker && !showBodyComMarkers)
    ) {
      group.parent?.remove(group);
      return undefined;
    }

    const markerRadii = computeMarkerRadii(markerParent);

    if (showRobotComMarker) {
      computeObjectComMarkers({ rows, bodies, worldBodies }).forEach((objectCom) => {
        group.add(
          createComMarker({
            color: ROBOT_COM_COLOR,
            radius: markerRadii.robot,
            name: `__massPropertiesObjectComMarker:${objectCom.rootUuid}`,
            position: objectCom.position,
            userData: {
              markerKind: "robotCom",
              bodyUuid: objectCom.rootUuid,
              label: `${objectCom.label} COM`,
            },
          })
        );
      });
    }

    if (showBodyComMarkers) {
      rows.forEach((row) => {
        if (!row?.bodyUuid || !isFiniteVector3(row.worldCom)) return;
        if (!(Number(row.mass) > 0 || Number(row.subtreeMass) > 0)) return;
        group.add(
          createComMarker({
            color: BODY_COM_COLOR,
            radius: markerRadii.body,
            name: `__massPropertiesBodyComMarker:${row.bodyUuid}`,
            position: row.worldCom,
            userData: {
              markerKind: "bodyCom",
              bodyUuid: row.bodyUuid,
              bodyName: row.displayName || row.name || "",
            },
          })
        );
      });
    }

    return () => {
      clearGroup(group);
      group.parent?.remove(group);
    };
  }, [
    computedAt,
    bodies,
    isStale,
    rows,
    sceneRef,
    showBodyComMarkers,
    showRobotComMarker,
    status,
    structureChangeTick,
    worldBodies,
  ]);
}
